import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea, Button, Stack,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, Tabs, Tab, LinearProgress, Divider, List,
  ListItem, ListItemText, ListItemIcon, Accordion, AccordionSummary, AccordionDetails,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import FavoriteIcon from '@mui/icons-material/Favorite';
import {
  HONEYMOON_PARTNERS_QUERY,
  HONEYMOON_PACKAGES_QUERY,
  MY_HONEYMOON_BOOKINGS_QUERY,
  CREATE_HONEYMOON_BOOKING_MUTATION,
  UPDATE_HONEYMOON_BOOKING_MUTATION,
  DELETE_HONEYMOON_BOOKING_MUTATION,
  ADD_HONEYMOON_DOCUMENT_MUTATION,
  DELETE_HONEYMOON_DOCUMENT_MUTATION,
} from '../graphql/queries';
import ContractedOfferingsPanel from '../components/ContractedOfferingsPanel';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const BOOKING_STATUS_COLOR = { enquiry: 'default', confirmed: 'info', paid: 'success', completed: 'primary', cancelled: 'error' };
const DOC_TYPES = ['itinerary', 'ticket', 'hotel_voucher', 'visa', 'passport', 'other'];
const DOC_ICONS = { itinerary: <BeachAccessIcon />, ticket: <FlightIcon />, hotel_voucher: <HotelIcon />, visa: <FolderSpecialIcon />, passport: <FolderSpecialIcon />, other: <AttachFileIcon /> };
const PKG_TYPE_COLOR = { budget: 'default', standard: 'info', premium: 'secondary', luxury: 'warning' };

const fmt = (n) => n != null ? ` ${Number(n).toLocaleString('en-IN')}` : '\u2014';

const EMPTY_BOOKING = {
  packageId: '', partnerId: '', destination: '', travelFrom: '', travelTo: '',
  roomType: '', guestCount: 2, totalCost: '', customAddons: '', notes: '',
};
const EMPTY_DOC = { docType: 'itinerary', title: '', fileUrl: '', fileName: '', notes: '' };

export default function HoneymoonPage() {
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');

  // Filters for packages tab
  const [destFilter, setDestFilter] = useState('');

  // Booking dialog
  const [bookDialog, setBookDialog] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [bookForm, setBookForm]       = useState(EMPTY_BOOKING);
  const [selectedPkg, setSelectedPkg] = useState(null);

  // Document dialog
  const [docDialog, setDocDialog]   = useState(false);
  const [docBookingId, setDocBookingId] = useState('');
  const [docForm, setDocForm]       = useState(EMPTY_DOC);

  const { data: partnersData } = useQuery(HONEYMOON_PARTNERS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: packagesData, loading: pkgLoading } = useQuery(HONEYMOON_PACKAGES_QUERY, {
    variables: destFilter ? { destination: destFilter } : {},
    fetchPolicy: 'cache-and-network',
  });
  const { data: bookingsData, loading: bookLoading, refetch: refetchBookings } = useQuery(MY_HONEYMOON_BOOKINGS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const partners = partnersData?.honeymoonPartners || [];
  const packages = packagesData?.honeymoonPackages || [];
  const bookings = bookingsData?.myHoneymoonBookings || [];

  const [createBooking] = useMutation(CREATE_HONEYMOON_BOOKING_MUTATION, {
    onCompleted: () => { setBookDialog(false); setBookForm(EMPTY_BOOKING); setSelectedPkg(null); refetchBookings(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateBooking] = useMutation(UPDATE_HONEYMOON_BOOKING_MUTATION, {
    onCompleted: () => { setBookDialog(false); setEditBooking(null); refetchBookings(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteBooking] = useMutation(DELETE_HONEYMOON_BOOKING_MUTATION, { onCompleted: () => refetchBookings() });

  const [addDoc]    = useMutation(ADD_HONEYMOON_DOCUMENT_MUTATION, {
    onCompleted: () => { setDocDialog(false); setDocForm(EMPTY_DOC); refetchBookings(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteDoc] = useMutation(DELETE_HONEYMOON_DOCUMENT_MUTATION, { onCompleted: () => refetchBookings() });

  const openBookPackage = (pkg) => {
    setEditBooking(null);
    setSelectedPkg(pkg);
    setBookForm({
      ...EMPTY_BOOKING,
      packageId: pkg.id,
      partnerId: pkg.partner?.id || '',
      destination: pkg.destination,
      roomType: pkg.roomTypes?.[0] || '',
      totalCost: pkg.basePrice || '',
    });
    setBookDialog(true);
  };
  const openEditBooking = (b) => {
    setEditBooking(b);
    setSelectedPkg(b.package || null);
    setBookForm({
      packageId: b.packageId || '', partnerId: b.partnerId || '', destination: b.destination || '',
      travelFrom: b.travelFrom || '', travelTo: b.travelTo || '', roomType: b.roomType || '',
      guestCount: b.guestCount || 2, totalCost: b.totalCost || '', customAddons: b.customAddons || '', notes: b.notes || '',
    });
    setBookDialog(true);
  };
  const handleSaveBooking = () => {
    const vars = { ...bookForm, guestCount: Number(bookForm.guestCount) || 2, totalCost: Number(bookForm.totalCost) || null, packageId: bookForm.packageId || null, partnerId: bookForm.partnerId || null };
    if (editBooking) updateBooking({ variables: { id: editBooking.id, bookingRef: editBooking.bookingRef, ...vars } });
    else createBooking({ variables: vars });
  };

  const openAddDoc = (bookingId) => { setDocBookingId(bookingId); setDocForm(EMPTY_DOC); setDocDialog(true); };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <FavoriteIcon color="error" />
            <Typography variant="h4" fontWeight={700}>Honeymoon Planner</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">Discover packages, book your dream honeymoon, and store all travel documents in one place.</Typography>
        </Box>
      </Stack>

      <ContractedOfferingsPanel categories={['honeymoon']} title="Contracted honeymoon partners" />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Discover Packages" />
        <Tab label={`My Bookings (${bookings.length})`} />
        <Tab label="Travel Partners" />
      </Tabs>

      {/* ── TAB 0: Discover Packages ──────────────────────────── */}
      {tab === 0 && (
        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
            <TextField
              label="Filter by destination" size="small" value={destFilter}
              onChange={e => setDestFilter(e.target.value)} placeholder="e.g. Maldives, Bali"
              sx={{ width: 240 }}
            />
            {destFilter && <Button size="small" onClick={() => setDestFilter('')}>Clear</Button>}
          </Stack>
          {pkgLoading && <LinearProgress sx={{ mb: 2 }} />}
          {packages.length === 0 && !pkgLoading && (
            <Alert severity="info">No packages available yet. Admin can add partners and packages from the admin panel.</Alert>
          )}
          <Grid container spacing={3}>
            <Paged items={packages}>{rows => rows.map(pkg => (
              <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', '&:hover': { boxShadow: 6 } }}>
                  <Box sx={{ bgcolor: 'primary.dark', p: 2, minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <BeachAccessIcon sx={{ fontSize: 48, color: 'white', opacity: 0.7 }} />
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6" fontWeight={700}>{pkg.name}</Typography>
                      <Chip label={pkg.packageType} size="small" color={PKG_TYPE_COLOR[pkg.packageType] || 'default'} />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <FlightIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">{pkg.destination} · {pkg.durationDays} days</Typography>
                    </Stack>
                    {pkg.partner && (
                      <Typography variant="caption" color="text.secondary">via {pkg.partner.name}</Typography>
                    )}
                    {pkg.basePrice && (
                      <Typography variant="h6" color="primary.main" fontWeight={700} sx={{ mt: 1 }}>{fmt(pkg.basePrice)}</Typography>
                    )}
                    {(pkg.inclusions || []).length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={1}>
                        {pkg.inclusions.slice(0, 4).map(inc => (
                          <Chip key={inc} label={inc} size="small" variant="outlined" />
                        ))}
                        {pkg.inclusions.length > 4 && <Chip label={`+${pkg.inclusions.length - 4} more`} size="small" />}
                      </Stack>
                    )}
                    {pkg.description && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>{pkg.description}</Typography>
                    )}
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button variant="contained" fullWidth startIcon={<AddIcon />} onClick={() => openBookPackage(pkg)}>
                      Book This Package
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {/* ── TAB 1: My Bookings ───────────────────────────────── */}
      {tab === 1 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" color="text.secondary">{bookings.length} booking{bookings.length !== 1 ? 's' : ''}</Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setEditBooking(null); setSelectedPkg(null); setBookForm(EMPTY_BOOKING); setBookDialog(true); }}>
              Custom Booking
            </Button>
          </Stack>
          {bookLoading && <LinearProgress sx={{ mb: 2 }} />}
          {bookings.length === 0 && !bookLoading && (
            <Alert severity="info">No bookings yet. Browse packages above or create a custom booking.</Alert>
          )}
          <Paged items={bookings}>{rows => rows.map(b => (
            <Accordion key={b.id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                  <FlightIcon color="primary" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography fontWeight={700}>{b.destination || b.package?.destination || 'Custom Booking'}</Typography>
                    {b.package && <Typography variant="caption" color="text.secondary">{b.package.name} · {b.package.durationDays} days</Typography>}
                  </Box>
                  {b.travelFrom && b.travelTo && (
                    <Typography variant="caption" color="text.secondary">{new Date(b.travelFrom).toLocaleDateString()} to {new Date(b.travelTo).toLocaleDateString()}</Typography>
                  )}
                  <Chip label={b.status} size="small" color={BOOKING_STATUS_COLOR[b.status] || 'default'} />
                  {b.bookingRef && <Chip label={`Ref: ${b.bookingRef}`} size="small" variant="outlined" />}
                  <Tooltip title="Add document"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openAddDoc(b.id); }}><AttachFileIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditBooking(b); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteBooking({ variables: { id: b.id } }); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Room Type</Typography>
                    <Typography variant="body2">{b.roomType || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Guests</Typography>
                    <Typography variant="body2">{b.guestCount}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Total Cost</Typography>
                    <Typography variant="body2" fontWeight={600}>{fmt(b.totalCost)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Paid</Typography>
                    <Typography variant="body2" color="success.main" fontWeight={600}>{fmt(b.paidAmount)}</Typography>
                  </Grid>
                  {b.partner && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Partner</Typography>
                      <Typography variant="body2">{b.partner.name} {b.partner.phone ? `· ${b.partner.phone}` : ''}</Typography>
                    </Grid>
                  )}
                  {b.customAddons && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Add-ons / Customizations</Typography>
                      <Typography variant="body2">{b.customAddons}</Typography>
                    </Grid>
                  )}
                </Grid>

                {/* Document Vault */}
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    <FolderSpecialIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    Document Vault ({(b.documents || []).length})
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => openAddDoc(b.id)}>Add Document</Button>
                </Stack>
                {(b.documents || []).length === 0 && (
                  <Typography variant="caption" color="text.secondary">No documents yet. Add tickets, itineraries, vouchers, etc.</Typography>
                )}
                <List dense disablePadding>
                  {(b.documents || []).map(doc => (
                    <ListItem key={doc.id} disableGutters secondaryAction={
                      <IconButton size="small" color="error" onClick={() => deleteDoc({ variables: { id: doc.id } })}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {DOC_ICONS[doc.docType] || <AttachFileIcon />}
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.title}
                        secondary={
                          <span>
                            <Chip label={doc.docType.replace('_', ' ')} size="small" sx={{ mr: 0.5 }} />
                            {doc.fileUrl && <a href={doc.fileUrl} target="_blank" rel="noreferrer" style={{ fontSize: '0.75rem' }}>View File</a>}
                            {doc.notes && ` · ${doc.notes}`}
                          </span>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}</Paged>
        </Box>
      )}

      {/* ── TAB 2: Travel Partners ───────────────────────────── */}
      {tab === 2 && (
        <Box>
          {partners.length === 0 && (
            <Alert severity="info">No travel partners listed yet. Partners are added by the admin.</Alert>
          )}
          <Grid container spacing={2}>
            <Paged items={partners}>{rows => rows.map(p => (
              <Grid item xs={12} sm={6} md={4} key={p.id}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography fontWeight={700}>{p.name}</Typography>
                      <Chip label={p.partnerType.replace('_', ' ')} size="small" />
                    </Stack>
                    {p.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{p.description}</Typography>}
                    {(p.destinations || []).length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={1}>
                        {p.destinations.map(d => <Chip key={d} label={d} size="small" icon={<FlightIcon />} />)}
                      </Stack>
                    )}
                    <Stack spacing={0.5}>
                      {p.phone   && <Typography variant="caption">Phone: {p.phone}</Typography>}
                      {p.email   && <Typography variant="caption">Email: {p.email}</Typography>}
                      {p.website && <Typography variant="caption"><a href={p.website} target="_blank" rel="noreferrer">{p.website}</a></Typography>}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {/* Booking Dialog */}
      <Dialog open={bookDialog} onClose={() => setBookDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editBooking ? 'Edit Booking' : selectedPkg ? `Book: ${selectedPkg.name}` : 'New Custom Booking'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {selectedPkg && !editBooking && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <BeachAccessIcon color="primary" />
                  <Box>
                    <Typography fontWeight={600}>{selectedPkg.name}</Typography>
                    <Typography variant="caption">{selectedPkg.destination} · {selectedPkg.durationDays} days · {fmt(selectedPkg.basePrice)}</Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
            <TextField label="Destination" value={bookForm.destination} onChange={e => setBookForm(f => ({ ...f, destination: e.target.value }))} fullWidth placeholder="e.g. Maldives, Bali, Switzerland" />
            <Stack direction="row" spacing={2}>
              <TextField label="Travel From" type="date" value={bookForm.travelFrom} onChange={e => setBookForm(f => ({ ...f, travelFrom: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Travel To"   type="date" value={bookForm.travelTo}   onChange={e => setBookForm(f => ({ ...f, travelTo: e.target.value }))}   fullWidth InputLabelProps={{ shrink: true }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Room Type" value={bookForm.roomType} onChange={e => setBookForm(f => ({ ...f, roomType: e.target.value }))} fullWidth placeholder="e.g. Deluxe Suite, Pool Villa" />
              <TextField label="Guests" type="number" value={bookForm.guestCount} onChange={e => setBookForm(f => ({ ...f, guestCount: e.target.value }))} fullWidth sx={{ maxWidth: 120 }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Total Cost (₹)" type="number" value={bookForm.totalCost} onChange={e => setBookForm(f => ({ ...f, totalCost: e.target.value }))} fullWidth />
            </Stack>
            <TextField label="Romantic Add-ons / Customizations" value={bookForm.customAddons} onChange={e => setBookForm(f => ({ ...f, customAddons: e.target.value }))} fullWidth multiline rows={2} placeholder="e.g. Candle-light dinner, Rose petal decoration, Couples spa" />
            <TextField label="Notes" value={bookForm.notes} onChange={e => setBookForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBookDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveBooking}>Save Booking</Button>
        </DialogActions>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={docDialog} onClose={() => setDocDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Document to Vault</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Document Type" value={docForm.docType} onChange={e => setDocForm(f => ({ ...f, docType: e.target.value }))} fullWidth>
              {DOC_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</MenuItem>)}
            </TextField>
            <TextField label="Title" value={docForm.title} onChange={e => setDocForm(f => ({ ...f, title: e.target.value }))} fullWidth required placeholder="e.g. Flight Ticket - SpiceJet SG-204" />
            <TextField label="File URL" value={docForm.fileUrl} onChange={e => setDocForm(f => ({ ...f, fileUrl: e.target.value }))} fullWidth placeholder="https://... (link to document)" />
            <TextField label="File Name" value={docForm.fileName} onChange={e => setDocForm(f => ({ ...f, fileName: e.target.value }))} fullWidth placeholder="e.g. flight_ticket.pdf" />
            <TextField label="Notes" value={docForm.notes} onChange={e => setDocForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDocDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={() => addDoc({ variables: { bookingId: docBookingId, ...docForm } })}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
